/**
 * RichEditorWithFeatures.jsx
 *
 * A self-contained rich text editor panel with:
 *  - ReactQuill (NO drag-and-drop)
 *  - Version history stored in DB via versionsApi prop
 *  - Role-based comment thread stored in DB via commentsApi prop
 *  - Diff view between versions
 *
 * Props:
 *   field          {string}   'abstract' | 'biography'
 *   label          {string}   display label
 *   value          {string}   current HTML value (controlled)
 *   onChange       {fn}       (value: string) => void
 *   error          {string}   validation error message
 *   currentUser    {object}   { name, role }  — role: 'admin' | 'event' | 'onsite'
 *   presentationId {string}   used as DB key
 *   required       {bool}
 *   versionsApi    {object}   { list, save }          — DB-backed
 *   commentsApi    {object}   { list, add, resolve, remove } — DB-backed
 */

import React, {
  useCallback, useEffect, useRef, useState, useMemo,
} from "react";
import ReactQuill, { Quill } from "react-quill";
// import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";

/* ─── tiny diff helper (no external dep) ──────────────────────────── */
function diffHtml(oldHtml, newHtml) {
  if (oldHtml === newHtml) return newHtml;
  const strip = (h) => h?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || "";
  const oldWords = strip(oldHtml).split(" ");
  const newWords = strip(newHtml).split(" ");
  const result = [];
  let oi = 0, ni = 0;
  while (ni < newWords.length) {
    if (oldWords[oi] === newWords[ni]) {
      result.push(`<span>${newWords[ni]}</span>`);
      oi++; ni++;
    } else {
      if (oi < oldWords.length && !newWords.slice(ni, ni + 3).includes(oldWords[oi])) {
        result.push(`<span style="background:#ffd6d6;text-decoration:line-through;color:#c0392b">${oldWords[oi]}</span>`);
        oi++;
      } else {
        result.push(`<span style="background:#d4f5e2;color:#1a6b3c">${newWords[ni]}</span>`);
        ni++;
      }
    }
  }
  while (oi < oldWords.length) {
    result.push(`<span style="background:#ffd6d6;text-decoration:line-through;color:#c0392b">${oldWords[oi]}</span>`);
    oi++;
  }
  return result.join(" ");
}

/* ─── role config ─────────────────────────────────────────────────────
 *
 *  admin  — full access: save versions, restore, add comments,
 *           mark instructions, resolve, delete
 *  event  — content creator: save versions, add regular comments only
 *  onsite — reviewer: add comments + mark as instruction, resolve
 *           (cannot restore versions or edit content)
 */
const ROLES = {
  admin:  { label: "Admin",       color: "#534AB7", bg: "#EEEDFE", canInstruct: true,  canRestore: true,  canDelete: true  },
  event:  { label: "Event User",  color: "#0F6E56", bg: "#E1F5EE", canInstruct: false, canRestore: true, canDelete: true },
  onsite: { label: "Onsite User", color: "#993C1D", bg: "#FAECE7", canInstruct: true,  canRestore: true, canDelete: true },
};

/* ─── Quill toolbar/modules config ────────────────────────────────── */
const buildModules = () => ({
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link", "image"],
      ["blockquote"],
      ['formula'],
      ["clean"],
    ],
  },
  clipboard: { matchVisual: false },
});

const FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "color", "background", "list", "bullet", "indent",
  "align", "link", "image", "blockquote", "code-block",
];

/* ─── Styles ───────────────────────────────────────────────────────── */
const SC = {
  wrap: {
    border: "1.5px solid #e3e8ef", borderRadius: 12,
    overflow: "hidden", fontFamily: "'DM Sans', sans-serif",
    transition: "border-color 0.15s",
  },
  wrapError: { borderColor: "#c0392b" },

  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 12px", background: "#f8fafb",
    borderBottom: "1px solid #e3e8ef", flexWrap: "wrap", gap: 6,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 6 },
  toolbarRight: { display: "flex", alignItems: "center", gap: 6 },

  iconBtn: (active) => ({
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 11px", borderRadius: 7, fontSize: 12, fontWeight: 600,
    cursor: "pointer", border: "1.5px solid",
    fontFamily: "'DM Sans', sans-serif", transition: "all 0.12s",
    background: active ? "#534AB7" : "#fff",
    borderColor: active ? "#534AB7" : "#e3e8ef",
    color: active ? "#fff" : "#4a5568",
  }),

  panel: {
    borderTop: "1px solid #e3e8ef",
    background: "#fff", maxHeight: 420, overflowY: "auto",
  },
  panelHeader: {
    padding: "12px 16px", borderBottom: "1px solid #f0f4f8",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    position: "sticky", top: 0, background: "#fff", zIndex: 1,
  },
  panelTitle: { fontSize: 13, fontWeight: 700, color: "#1a2332" },

  versionItem: (active) => ({
    padding: "10px 16px", borderBottom: "1px solid #f0f4f8",
    display: "flex", alignItems: "flex-start", gap: 12,
    background: active ? "#f5f4fe" : "#fff",
    transition: "background 0.1s",
  }),
  versionNum: {
    fontFamily: "monospace", fontSize: 11, fontWeight: 700,
    color: "#534AB7", background: "#EEEDFE",
    padding: "2px 7px", borderRadius: 5, whiteSpace: "nowrap",
    alignSelf: "flex-start", marginTop: 2,
  },
  versionMeta: { flex: 1 },
  versionNote: { fontSize: 13, fontWeight: 600, color: "#1a2332", marginBottom: 2 },
  versionTime: { fontSize: 11, color: "#9aa5b4" },

  restoreBtn: (readOnly,isRestricted) => ({
    padding: "4px 11px", borderRadius: 6, fontSize: 11.5, fontWeight: 600,
    cursor: readOnly || isRestricted ? "not-allowed" : "pointer", border: "1.5px solid #d5d2f5",
    background: "#EEEDFE", color: "#534AB7",
    fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
  }),

  diffBtn: {
    padding: "4px 11px", borderRadius: 6, fontSize: 11.5, fontWeight: 600,
    cursor: "pointer", border: "1.5px solid #e3e8ef",
    background: "#f8fafb", color: "#4a5568",
    fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
  },

  diffBox: {
    padding: "14px 16px", background: "#fafcfe",
    borderBottom: "1px solid #e3e8ef", lineHeight: 1.8, fontSize: 13.5,
  },
  diffLegend: {
    display: "flex", gap: 14, padding: "8px 16px",
    borderBottom: "1px solid #f0f4f8", background: "#f8fafb",
    alignItems: "center",
  },
  diffLegendItem: { display: "flex", alignItems: "center", gap: 5, fontSize: 11.5 },
  diffDot: (color) => ({ width: 10, height: 10, borderRadius: 2, background: color }),

  commentThread: { padding: "10px 14px", display: "flex", flexDirection: "column", gap: 10 },
  commentBubble: (role) => ({
    background: ROLES[role]?.bg || "#f8fafb",
    borderLeft: `3px solid ${ROLES[role]?.color || "#e3e8ef"}`,
    borderRadius: "0 8px 8px 0",
    padding: "10px 12px",
  }),
  replyBubble: (role) => ({
    background: ROLES[role]?.bg || "#f8fafb",
    borderLeft: `3px solid ${ROLES[role]?.color || "#e3e8ef"}`,
    borderRadius: "0 6px 6px 0",
    padding: "8px 10px",
    marginTop: 6,
    marginLeft: 16,
    opacity: 0.92,
  }),
  commentMeta: {
    display: "flex", alignItems: "center", gap: 8,
    marginBottom: 5, flexWrap: "wrap",
  },
  commentAuthor: { fontWeight: 700, fontSize: 12.5, color: "#1a2332" },
  rolePill: (role) => ({
    fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20,
    background: ROLES[role]?.bg || "#f0f4f8",
    color: ROLES[role]?.color || "#6b7a8d",
    border: `1px solid ${ROLES[role]?.color || "#e3e8ef"}30`,
  }),
  commentTime: { fontSize: 11, color: "#b0bec8", marginLeft: "auto" },
  commentText: { fontSize: 13, lineHeight: 1.55, color: "#2d3748" },
  instructionBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
    background: "#fff3cd", color: "#8a6d00", border: "1px solid #f0d080",
    marginBottom: 5,
  },
  resolvedBadge: {
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
    background: "#E1F5EE", color: "#0F6E56", marginBottom: 5,
  },
  commentActions: { display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" },
  commentActionBtn: (variant) => ({
    padding: "3px 10px", borderRadius: 5, fontSize: 11, fontWeight: 600,
    cursor: "pointer", border: "1.5px solid",
    fontFamily: "'DM Sans', sans-serif", transition: "all 0.12s",
    ...(variant === "resolve"  ? { background: "#E1F5EE",  color: "#0F6E56",  borderColor: "#9FE1CB" }  :
        variant === "reply"    ? { background: "#f0f4f8",  color: "#4a5568",  borderColor: "#e3e8ef" }  :
        variant === "delete"   ? { background: "#fff1f0",  color: "#c0392b",  borderColor: "#f5c6c3" }  :
        /* flag/instruction */   { background: "#fff3cd",  color: "#8a6d00",  borderColor: "#f0d080" }),
  }),

  /* inline reply input area */
  replyInputWrap: {
    marginTop: 8, marginLeft: 16, display: "flex", flexDirection: "column", gap: 6,
  },
  replyInput: {
    width: "100%", padding: "7px 10px", border: "1.5px solid #e3e8ef",
    borderRadius: 7, fontSize: 12.5, outline: "none",
    fontFamily: "'DM Sans', sans-serif", color: "#1a2332",
    resize: "vertical", minHeight: 52, transition: "border-color 0.15s",
    boxSizing: "border-box",
  },
  replyActions: { display: "flex", gap: 6 },

  addCommentBox: {
    padding: "12px 14px", borderTop: "1px solid #f0f4f8",
    background: "#fafcfe",
    position: "sticky", bottom: 0,
  },
  addCommentHeader: {
    display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap",
  },
  commentInput: {
    width: "100%", padding: "8px 12px", border: "1.5px solid #e3e8ef",
    borderRadius: 8, fontSize: 13, outline: "none",
    fontFamily: "'DM Sans', sans-serif", color: "#1a2332", resize: "vertical",
    minHeight: 64, marginBottom: 8, transition: "border-color 0.15s",
    boxSizing: "border-box",
  },
  postBtn: (disabled) => ({
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "7px 16px",
    background: disabled ? "#c8d0da" : "#534AB7",
    color: "#fff",
    border: "none", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "'DM Sans', sans-serif",
    transition: "background 0.15s",
  }),
  miniInput: {
    padding: "5px 10px", border: "1.5px solid #e3e8ef",
    borderRadius: 7, fontSize: 12, outline: "none",
    fontFamily: "'DM Sans', sans-serif", color: "#1a2332",
    transition: "border-color 0.15s", minWidth: 110,
  },
  miniSelect: {
    padding: "5px 8px", border: "1.5px solid #e3e8ef",
    borderRadius: 7, fontSize: 12, outline: "none",
    fontFamily: "'DM Sans', sans-serif", background: "#fff",
  },
  errorMsg: { fontSize: 12, color: "#c0392b", padding: "4px 12px 8px" },
  loadingBox: {
    padding: "24px 16px", textAlign: "center", color: "#9aa5b4", fontSize: 13,
  },
};

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════ */
export default function RichEditorWithFeatures({
  field,
  label,
  value,
  onChange,
  error,
  currentUser: currentUserProp,
  presentationId = "default",
  required = false,
  readOnly = false,
  versionsApi = null,
  commentsApi = null,
  type ,
  isRestricted = false, 
  reloadHistory = 0
}) {
  /* ── resolve currentUser inside component (not at module level) ── */
  const currentUser = useMemo(() => {
    if (currentUserProp?.name && currentUserProp?.role) return currentUserProp;
    /* fallback: read from localStorage only if prop not provided */
    const name = localStorage.getItem("sub_domain") || "Unknown";
    const role = localStorage.getItem("role") || "event";
    return { name, role };
  }, [currentUserProp]);

  const quillRef = useRef(null);

  const [panel,         setPanel]         = useState(null); // null | 'versions' | 'comments'
  const [versions,      setVersions]      = useState([]);
  const [comments,      setComments]      = useState([]);
  const [vLoading,      setVLoading]      = useState(false);
  const [cLoading,      setCLoading]      = useState(false);
  const [diffTarget,    setDiffTarget]    = useState(null);
  const [commentText,   setCommentText]   = useState("");
  const [commentAuthor, setCommentAuthor] = useState("");
  const [commentRole,   setCommentRole]   = useState("");
  const [isInstruction, setIsInstruction] = useState(false);
  /* replyTo: null | { commentId, parentAuthor } */
  const [replyTo,       setReplyTo]       = useState(null);
  const [replyText,     setReplyText]     = useState("");

  // const [savingVersion, setSavingVersion] = useState(false);

  /* sync author/role from currentUser once resolved */
  useEffect(() => {
    setCommentAuthor(currentUser.name || "");
    setCommentRole(currentUser.role || "event");
  }, [currentUser]);

  const modules = useMemo(() => buildModules(), []);

  /* ── permission flags (derived from currentUser.role) ─────────── */
  const roleConfig  = ROLES[currentUser.role] || ROLES.event;
  const isAdmin     = currentUser.role === "admin"|| "onsite";
  const isEventUser = currentUser.role === "event";
  const isOnsite    = currentUser.role === "onsite";
  /* event user can edit their own content; admin can always edit */
  const canEdit     = isAdmin || isEventUser || isOnsite;
  /* who can restore a previous version */
  const canRestore  = roleConfig.canRestore;
  /* who can mark a comment as instruction */
  const canInstruct = roleConfig.canInstruct;
  /* who can resolve/delete comments */
  const canResolve  = isAdmin || isOnsite;
  const canDelete   = roleConfig.canDelete || isOnsite;

  /* ── unresolved instruction count (badge on Comments button) ───── */
  const unresolvedCount = useMemo(
    () => comments.filter((c) => !c.resolved && (c.isInstruction || c.type === "instruction")).length,
    [comments]
  );

  /* ── format timestamp ─────────────────────────────────────────── */
  const fmt = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("en-IN", {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  /* ── DB loaders ───────────────────────────────────────────────── */
  const loadVersions = useCallback(async () => {
    if (!versionsApi || !presentationId || presentationId === "new") return;
    setVLoading(true);
    try {
      const data = await versionsApi.list(presentationId, field);
      setVersions(Array.isArray(data) ? data : []);
    } finally {
      setVLoading(false);
    }
  }, [versionsApi, presentationId, field]);

  const loadComments = useCallback(async () => {
    if (!commentsApi || !presentationId || presentationId === "new") return;
    setCLoading(true);
    try {
      const data = await commentsApi.list(presentationId, field);
      setComments(Array.isArray(data) ? data : []);
    } finally {
      setCLoading(false);
    }
  }, [commentsApi, presentationId, field]);

   useEffect(() => {
    if (type === "edit") {
      (async () => {
         await loadVersions();
         await loadComments();
      })();
    }
  }, [type,reloadHistory]);

  /* ── panel toggle ─────────────────────────────────────────────── */
  const togglePanel = (name) => {
    if (panel === name) { setPanel(null); return; }
    setPanel(name);
    setDiffTarget(null);
    setReplyTo(null);
    if (name === "versions") loadVersions();
    if (name === "comments") loadComments();
  };


  /* ── restore version ──────────────────────────────────────────── */
  const restoreVersion = useCallback((ver) => {
    Swal.fire({
      icon: "question",
      title: "Restore this version?",
      text: "Current content will be replaced.",
      showCancelButton: true,
      confirmButtonColor: "#534AB7",
      confirmButtonText: "Yes, restore",
    }).then((result) => {
      if (result.isConfirmed) {
        onChange(ver.content);
        setPanel(null);
        Swal.fire({ icon: "info", title: "Restored", text: "Remember to save the form.", timer: 1800, showConfirmButton: false });
      }
    });
  }, [onChange]);

  /* ── post top-level comment ───────────────────────────────────── */
  const postComment = useCallback(async () => {
    if (!commentText.trim() || !commentsApi) return;
    if (!presentationId || presentationId === "new") {
      Swal.fire({ icon: "warning", title: "Save the presentation first", timer: 2200, showConfirmButton: false });
      return;
    }
    await commentsApi.add(presentationId, field, {
      author:        commentAuthor,
      role:          commentRole,
      text:          commentText.trim(),
      type:          canInstruct && isInstruction ? "instruction" : "comment",
      isInstruction: canInstruct && isInstruction,
      parentId:      null,
    });
    setCommentText("");
    setIsInstruction(false);
    await loadComments();
  }, [
    commentText, commentAuthor, commentRole, isInstruction,
    canInstruct, commentsApi, presentationId, field, loadComments,
  ]);

  /* ── post reply ───────────────────────────────────────────────── */
  const postReply = useCallback(async (parentId) => {
    if (!replyText.trim() || !commentsApi) return;
    if (!presentationId || presentationId === "new") {
      Swal.fire({ icon: "warning", title: "Save the presentation first", timer: 2200, showConfirmButton: false });
      return;
    }
    await commentsApi.add(presentationId, field, {
      author:        currentUser.name,
      role:          currentUser.role,
      text:          replyText.trim(),
      type:          "comment",
      isInstruction: false,
      parentId,
    });
    setReplyTo(null);
    setReplyText("");
    await loadComments();
  }, [replyText, currentUser, commentsApi, presentationId, field, loadComments]);

  /* ── resolve / delete ─────────────────────────────────────────── */
  const resolveComment = useCallback(async (id) => {
    if (!commentsApi) return;
    await commentsApi.resolve(id, currentUser.name);
    await loadComments();
  }, [commentsApi, currentUser.name, loadComments]);

  const deleteCommentEntry = useCallback(async (id) => {
    if (!commentsApi) return;
    const confirm = await Swal.fire({
      icon: "warning", title: "Delete comment?",
      showCancelButton: true, confirmButtonColor: "#c0392b", confirmButtonText: "Delete",
    });
    if (confirm.isConfirmed) {
      await commentsApi.remove(id);
      await loadComments();
    }
  }, [commentsApi, loadComments]);

  /* ── diff ─────────────────────────────────────────────────────── */
  const diffVersion = versions.find((v) => v.id === diffTarget);
  const diffResult  = diffVersion ? diffHtml(diffVersion.content, value) : null;

  /* ── separate top-level comments from replies ─────────────────── */
  const topLevelComments = useMemo(
    () => comments.filter((c) => !c.parentId),
    [comments]
  );
  const repliesFor = (parentId) => comments.filter((c) => c.parentId === parentId);

  /* ══ RENDER ════════════════════════════════════════════════════ */
  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── label row ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <label style={{ fontSize: 12.5, fontWeight: 700, color: "#4a5568", letterSpacing: 0.2 }}>
          {label}
          {required && <span style={{ color: "#c0392b", marginLeft: 2 }}>*</span>}
        </label>
        {unresolvedCount > 0 && (
          <span style={{
            background: "#fff3cd", color: "#8a6d00", border: "1px solid #f0d080",
            fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
            display: "inline-flex", alignItems: "center", gap: 4,
          }}>
            ⚠ {unresolvedCount} pending instruction{unresolvedCount > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* ── main card ── */}
      <div style={{ ...SC.wrap, ...(error ? SC.wrapError : {}) }}>
      

        {/* ── diff banner ── */}
        {diffResult && (
          <div>
            <div style={SC.diffLegend}>
              <div style={SC.diffLegendItem}>
                <div style={SC.diffDot("#d4f5e2")} />
                <span style={{ fontSize: 11, color: "#1a6b3c" }}>Added</span>
              </div>
              <div style={SC.diffLegendItem}>
                <div style={SC.diffDot("#ffd6d6")} />
                <span style={{ fontSize: 11, color: "#c0392b" }}>Removed</span>
              </div>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "#9aa5b4" }}>
                Comparing with snapshot — {fmt(diffVersion.saved_at || diffVersion.createdAt)}
              </span>
              <button
                type="button"
                onClick={() => setDiffTarget(null)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#c0392b", fontWeight: 600 }}
              >
                ✕ Close diff
              </button>
            </div>
            <div style={SC.diffBox} dangerouslySetInnerHTML={{ __html: diffResult }} />
          </div>
        )}

        {/* ── Quill editor ── */}
        <div style={{ position: "relative" }}>
          {!canEdit && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 10,
              background: "rgba(248,250,251,0.55)", cursor: "not-allowed",
              display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
              padding: 8,
            }}>
              <span style={{
                fontSize: 11, fontWeight: 700, background: "#FAECE7",
                color: "#993C1D", border: "1px solid #F0997B",
                padding: "2px 10px", borderRadius: 20,
              }}>
                Read-only — add comments below
              </span>
            </div>
          )}
          <div>
          <ReactQuill
            ref={quillRef}
            value={value}
            onChange={canEdit ? onChange : () => {}}
            readOnly={!canEdit || readOnly || isRestricted === true}
            theme="snow"
            modules={modules}
            formats={FORMATS}
            placeholder={`Enter ${label.toLowerCase()} here…`}
            style={{ minHeight: 250 }}
          />

          <style>
            {`
              .ql-container {
                  overflow: hidden;        
                }
                .ql-editor {
                  min-height: 250px;       
                  max-height: 480px;
                  overflow-y: auto;
                }
                .ql-snow .ql-editor blockquote {
                  border-left: 4px solid #ccc;
                  margin-bottom: 5px;
                  margin-top: 5px;
                  padding: 2px 2px 2px 16px !important;
                  background-color: #ededed !important;
                }
            `}
          </style>
        </div>
        </div>
         {/* ── toolbar strip ── */}
        <div style={SC.toolbar}>
          
          <div style={SC.toolbarLeft}>
            <button
              type="button"
              style={SC.iconBtn(panel === "versions")}
              onClick={() => togglePanel("versions")}
            >
              🕐 History
              {versions.length > 0 && (
                <span style={{
                  background: panel === "versions" ? "rgba(255,255,255,0.25)" : "#EEEDFE",
                  color: panel === "versions" ? "#fff" : "#534AB7",
                  fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, marginLeft: 2,
                }}>
                  {versions.length}
                </span>
              )}
            </button>
            {/* <button
              type="button"
              style={SC.iconBtn(panel === "comments")}
              onClick={() => togglePanel("comments")}
            >
              💬 Comments
              {comments.length > 0 && (
                <span style={{
                  background: unresolvedCount > 0
                    ? (panel === "comments" ? "rgba(255,255,255,0.25)" : "#fff3cd")
                    : (panel === "comments" ? "rgba(255,255,255,0.25)" : "#f0f4f8"),
                  color: unresolvedCount > 0
                    ? (panel === "comments" ? "#fff" : "#8a6d00")
                    : (panel === "comments" ? "#fff" : "#6b7a8d"),
                  fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, marginLeft: 2,
                }}>
                  {comments.length}
                </span>
              )}
            </button> */}
          </div>
        </div>

        {/* ═══ Version History panel ════════════════════════════════ */}
        {panel === "versions" && (
          <div style={SC.panel}>
            <div style={SC.panelHeader}>
              <span style={SC.panelTitle}>
                🕐 Version History — {versions.length} snapshot{versions.length !== 1 ? "s" : ""}
              </span>
            </div>
            {vLoading ? (
              <div style={SC.loadingBox}>Loading versions…</div>
            ) : versions.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center", color: "#9aa5b4", fontSize: 13 }}>
                No versions saved yet. Click "💾 Save Version" to create a snapshot.
              </div>
            ) : (
              [...versions].reverse().map((ver, i) => (
                <div key={ver.id || i} style={SC.versionItem(diffTarget === ver.id)}>
                  <span style={SC.versionNum}>v{versions.length - i}</span>
                  <div style={SC.versionMeta}>
                    <div style={SC.versionNote}>{ver.saved_by || ver.author || "Unknown"}</div>
                    <span style={SC.versionTime}>{fmt(ver.saved_at || ver.savedAt || ver.createdAt)}</span>
                    <div
                      style={{ marginTop: 5, fontSize: 12, color: "#6b7a8d", lineHeight: 1.4, maxHeight: 44, overflow: "hidden" }}
                      dangerouslySetInnerHTML={{
                        __html: (ver.content || "").replace(/<[^>]*>/g, " ").slice(0, 120) + "…",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                    <button
                      type="button"
                      style={SC.diffBtn}
                      onClick={() => setDiffTarget(diffTarget === ver.id ? null : ver.id)}
                    >
                      {diffTarget === ver.id ? "Hide diff" : "Diff"}
                    </button>
                    {canRestore && (
                      <button type="button"  disabled={readOnly === true ? true:false || isRestricted === true} style={SC.restoreBtn(readOnly || isRestricted === true)} onClick={() => restoreVersion(ver)}>
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ═══ Comments panel ═══════════════════════════════════════ */}
        {panel === "comments" && (
          <div style={SC.panel}>
            <div style={SC.panelHeader}>
              <span style={SC.panelTitle}>
                💬 Comments &amp; Instructions — {topLevelComments.length}
                {unresolvedCount > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "#8a6d00", fontWeight: 700 }}>
                    ({unresolvedCount} unresolved)
                  </span>
                )}
              </span>
            </div>

            {/* ── thread ── */}
            {cLoading ? (
              <div style={SC.loadingBox}>Loading comments…</div>
            ) : topLevelComments.length === 0 ? (
              <div style={{ padding: "24px 16px", textAlign: "center", color: "#9aa5b4", fontSize: 13 }}>
                No comments yet.
              </div>
            ) : (
              <div style={SC.commentThread}>
                {topLevelComments.map((c, i) => {
                  const cRole     = ROLES[c.role] ? c.role : "event";
                  const cIsInstr  = c.isInstruction || c.type === "instruction";
                  const cResolved = !!c.resolved;
                  const replies   = repliesFor(c.id);
                  const isReplying = replyTo?.commentId === c.id;

                  return (
                    <div key={c.id || i}>
                      {/* ── comment bubble ── */}
                      <div style={SC.commentBubble(cRole)}>
                        {cIsInstr && !cResolved && (
                          <div style={SC.instructionBadge}>⚠ Instruction — change required</div>
                        )}
                        {cResolved && (
                          <div style={SC.resolvedBadge}>
                            ✓ Resolved{c.resolvedBy ? ` by ${c.resolvedBy}` : ""}
                          </div>
                        )}
                        <div style={SC.commentMeta}>
                          <span style={SC.commentAuthor}>{c.author}</span>
                          <span style={SC.rolePill(cRole)}>{ROLES[cRole].label}</span>
                          <span style={SC.commentTime}>{fmt(c.created_at || c.createdAt)}</span>
                        </div>
                        <div style={SC.commentText}>{c.text}</div>

                        <div style={SC.commentActions}>
                          {/* resolve — admin or onsite, only on instructions */}
                          {cIsInstr && !cResolved && canResolve && (
                            <button
                              type="button"
                              style={SC.commentActionBtn("resolve")}
                              onClick={() => resolveComment(c.id)}
                            >
                              ✓ Mark resolved
                            </button>
                          )}
                          {/* reply — all roles */}
                          {!isReplying && (
                            <button
                              type="button"
                              style={SC.commentActionBtn("reply")}
                              onClick={() => { setReplyTo({ commentId: c.id, parentAuthor: c.author }); setReplyText(""); }}
                            >
                              ↩ Reply
                            </button>
                          )}
                          {/* delete — admin only */}
                          {/* {canDelete && (
                            <button
                              type="button"
                              style={SC.commentActionBtn("delete")}
                              onClick={() => deleteCommentEntry(c.id)}
                            >
                              ✕ Delete
                            </button>
                          )} */}
                        </div>
                      </div>

                      {/* ── existing replies ── */}
                      {replies.map((r, ri) => {
                        const rRole = ROLES[r.role] ? r.role : "event";
                        return (
                          <div key={r.id || ri} style={SC.replyBubble(rRole)}>
                            <div style={SC.commentMeta}>
                              <span style={SC.commentAuthor}>{r.author}</span>
                              <span style={SC.rolePill(rRole)}>{ROLES[rRole].label}</span>
                              <span style={SC.commentTime}>{fmt(r.created_at || r.createdAt)}</span>
                            </div>
                            <div style={{ ...SC.commentText, fontSize: 12.5 }}>{r.text}</div>
                            {canDelete && (
                              <div style={SC.commentActions}>
                                <button
                                  type="button"
                                  style={SC.commentActionBtn("delete")}
                                  onClick={() => deleteCommentEntry(r.id)}
                                >
                                  ✕ Delete
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* ── inline reply input ── */}
                      {isReplying && (
                        <div style={SC.replyInputWrap}>
                          <span style={{ fontSize: 11.5, color: "#9aa5b4" }}>
                            Replying to {replyTo.parentAuthor}
                          </span>
                          <textarea
                            style={SC.replyInput}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write a reply…"
                            onFocus={(e) => (e.target.style.borderColor = "#534AB7")}
                            onBlur={(e)  => (e.target.style.borderColor = "#e3e8ef")}
                            autoFocus
                          />
                          <div style={SC.replyActions}>
                            <button
                              type="button"
                              style={SC.postBtn(!replyText.trim())}
                              disabled={!replyText.trim()}
                              onClick={() => postReply(c.id)}
                            >
                              ✉ Post reply
                            </button>
                            <button
                              type="button"
                              style={{ ...SC.commentActionBtn("reply"), padding: "6px 14px" }}
                              onClick={() => { setReplyTo(null); setReplyText(""); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── add top-level comment form ── */}
            <div style={SC.addCommentBox}>
              <div style={SC.addCommentHeader}>
                {/* <input
                  style={SC.miniInput}
                  value={commentAuthor}
                  onChange={(e) => setCommentAuthor(e.target.value)}
                  placeholder="Your name"
                  onFocus={(e) => (e.target.style.borderColor = "#534AB7")}
                  onBlur={(e)  => (e.target.style.borderColor = "#e3e8ef")}
                /> */}
                {/* role selector only visible to admin (others always post as their own role) */}
                {/* {isAdmin ? (
                  <select
                    style={SC.miniSelect}
                    value={commentRole}
                    onChange={(e) => setCommentRole(e.target.value)}
                  >
                    {Object.entries(ROLES).map(([key, r]) => (
                      <option key={key} value={key}>{r.label}</option>
                    ))}
                  </select>
                ) : (
                  <span style={SC.rolePill(currentUser.role)}>
                    {roleConfig.label}
                  </span>
                )} */}
                {/* instruction toggle — onsite and admin only */}
                {/* {canInstruct && (
                  <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12.5, cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={isInstruction}
                      onChange={(e) => setIsInstruction(e.target.checked)}
                      style={{ accentColor: "#c4912a" }}
                    />
                    <span style={{ color: "#8a6d00" }}>⚠ Mark as instruction</span>
                  </label>
                )} */}
              </div>
              <textarea
                style={SC.commentInput}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={
                  canInstruct
                    ? "Add a review note or correction instruction…"
                    : "Add a comment or question…"
                }
                onFocus={(e) => (e.target.style.borderColor = "#534AB7")}
                onBlur={(e)  => (e.target.style.borderColor = "#e3e8ef")}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11.5, color: "#9aa5b4" }}>
                  {isInstruction
                    ? "⚠ Program Manager user will see this as a required change"
                    : "Regular comment — visible to all"}
                </span>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={SC.postBtn(!commentText.trim())}
                  onClick={postComment}
                  disabled={!commentText.trim()}
                >
                  ✉ Post
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && <div style={SC.errorMsg}>{error}</div>}
    </div>
  );
}